import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Calendar, Trophy, Users, ChevronRight, ArrowRight, RefreshCw, ExternalLink } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HomePage = () => {
  const [articles, setArticles] = useState([]);
  const [liveData, setLiveData] = useState(null);
  const [lastResult, setLastResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchLiveData = async (forceRefresh = false) => {
    try {
      if (forceRefresh) setRefreshing(true);
      const response = await axios.get(`${API}/live-matches?refresh=${forceRefresh}`);
      setLiveData(response.data);
    } catch (error) {
      console.error("Error fetching live data:", error);
    } finally {
      setRefreshing(false);
    }
  };

  const fetchLastResult = async () => {
    try {
      const response = await axios.get(`${API}/last-result`);
      setLastResult(response.data);
    } catch (error) {
      console.error("Error fetching last result:", error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [articlesRes] = await Promise.all([
          axios.get(`${API}/articles?limit=4`),
        ]);
        setArticles(articlesRes.data);
        await Promise.all([fetchLiveData(), fetchLastResult()]);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Auto-refresh every 30 minutes
  useEffect(() => {
    const interval = setInterval(() => fetchLiveData(true), 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const upcomingMatches = liveData?.upcoming || [];
  const classifica = liveData?.classifica || [];

  return (
    <div data-testid="home-page">
      <Navbar transparent />

      {/* Hero Section */}
      <section className="hero-section" data-testid="hero-section">
        <div
          className="hero-bg"
          style={{
            backgroundImage: `url(https://customer-assets.emergentagent.com/job_fef51c73-3fe9-4a0a-b70a-2a7119fa5032/artifacts/hux65oij_e24f5dc2-3c51-4269-9dc2-3795ec88c2d6.jpeg)`,
          }}
        />
        <div className="hero-overlay absolute inset-0" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 text-center text-white">
          <div className="animate-fade-in-up">
            {/* Logo in evidenza */}
            <div className="mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_volpiano-calcio/artifacts/8nz2bql1_IMG_8381.webp" 
                alt="G.S.D. Volpiano Pianese"
                className="h-24 sm:h-28 lg:h-32 w-auto mx-auto drop-shadow-2xl rounded-2xl"
                style={{ background: 'transparent' }}
                data-testid="hero-logo"
              />
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-black uppercase tracking-tight mb-4">
              Volpianopianese
            </h1>
            <p className="text-base lg:text-lg text-slate-300 max-w-2xl mx-auto mb-8">
              G.S.D. Volpianopianese - Eccellenza Piemonte Girone A
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/prima-squadra"
                data-testid="hero-cta-primary"
                className="btn-primary inline-flex items-center justify-center gap-2"
              >
                Scopri la Squadra
                <ArrowRight className="w-5 h-5" />
              </Link>
              <Link
                to="/giovanili"
                data-testid="hero-cta-secondary"
                className="btn-secondary inline-flex items-center justify-center gap-2"
              >
                Settore Giovanile
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-[#0047AB] py-8" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-3 gap-6 text-white text-center">
            {[
              { value: "100+", label: "Anni di Storia", icon: Trophy },
              { value: "200+", label: "Tesserati", icon: Users },
              { value: "3", label: "Trofei Vinti", icon: Trophy },
            ].map((stat, index) => (
              <div
                key={index}
                className="animate-fade-in-up"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <stat.icon className="w-8 h-8 mx-auto mb-2 opacity-80" />
                <div className="font-accent text-3xl lg:text-4xl font-extrabold">
                  {stat.value}
                </div>
                <div className="text-sm text-blue-200">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Live Matches Section */}
      <section className="section-padding bg-white" data-testid="matches-section">
        <div className="max-w-7xl mx-auto px-4">
          {/* Header with refresh button */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl lg:text-3xl font-bold text-slate-900">
                Eccellenza Piemonte - Girone A
              </h2>
              <p className="text-slate-500 text-sm mt-1">
                Dati aggiornati da iamcalcio.it
                {liveData?.last_update && (
                  <span className="ml-2 text-xs">
                    (ultimo aggiornamento: {new Date(liveData.last_update).toLocaleString('it-IT')})
                  </span>
                )}
              </p>
            </div>
            <button
              onClick={() => fetchLiveData(true)}
              disabled={refreshing}
              className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-full text-sm font-medium transition-colors"
              data-testid="refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              Aggiorna
            </button>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Upcoming Matches */}
            <div>
              <div className="flex items-center gap-2 mb-6">
                <Calendar className="w-5 h-5 text-[#0047AB]" />
                <h3 className="text-xl font-bold text-slate-900">Prossima Partita</h3>
              </div>
              <div className="space-y-4">
                {loading ? (
                  <div className="animate-pulse space-y-4">
                    <div className="h-32 bg-slate-200 rounded-xl" />
                  </div>
                ) : upcomingMatches.length > 0 ? (
                  upcomingMatches.slice(0, 1).map((match, idx) => (
                    <div
                      key={idx}
                      data-testid={`upcoming-match-${idx}`}
                      className="bg-gradient-to-br from-[#0047AB] to-[#003380] text-white p-6 rounded-xl shadow-lg"
                    >
                      <div className="text-center mb-3">
                        <span className="text-xs font-medium bg-white/20 px-3 py-1 rounded-full">
                          {match.competition || "Eccellenza Piemonte"}
                        </span>
                      </div>
                      
                      <div className="text-center space-y-3">
                        <div className={`font-bold text-xl ${match.home_team === 'Volpianopianese' ? 'text-yellow-300' : 'text-white'}`}>
                          {match.home_team}
                        </div>
                        
                        <div className="flex items-center justify-center gap-4">
                          <div className="h-px bg-white/30 flex-1" />
                          <span className="text-lg font-bold opacity-80">VS</span>
                          <div className="h-px bg-white/30 flex-1" />
                        </div>
                        
                        <div className={`font-bold text-xl ${match.away_team === 'Volpianopianese' ? 'text-yellow-300' : 'text-white'}`}>
                          {match.away_team}
                        </div>
                      </div>
                      
                      <div className="mt-4 pt-4 border-t border-white/20 text-center">
                        {(match.date || match.date_text) && (
                          <div className="text-sm opacity-90 mb-1">
                            {match.date_text || new Date(match.date).toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })}
                          </div>
                        )}
                        {match.time && (
                          <div className="text-2xl font-bold">{match.time}</div>
                        )}
                        {match.location && (
                          <p className="text-white/70 text-xs mt-2">
                            📍 {match.location}
                          </p>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="match-card text-center py-8">
                    <p className="text-slate-500">
                      Nessuna partita in programma
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Recent Results - Solo ultimo (Manuale) */}
            <div>
              <div className="flex items-center gap-2 mb-6">
                <Trophy className="w-5 h-5 text-[#0047AB]" />
                <h3 className="text-xl font-bold text-slate-900">Ultimo Risultato</h3>
              </div>
              <div className="space-y-3">
                {loading ? (
                  <div className="animate-pulse space-y-3">
                    <div className="h-24 bg-slate-200 rounded-xl" />
                  </div>
                ) : lastResult ? (
                  (() => {
                    const match = lastResult;
                    const isVolpianoHome = match.home_team.toLowerCase().includes('volpiano');
                    const volpianoScore = isVolpianoHome ? match.home_score : match.away_score;
                    const opponentScore = isVolpianoHome ? match.away_score : match.home_score;
                    const result = volpianoScore > opponentScore ? 'win' : volpianoScore < opponentScore ? 'loss' : 'draw';
                    
                    return (
                      <div
                        data-testid="last-result"
                        className={`bg-white rounded-xl p-6 shadow-sm border-l-4 ${
                          result === 'win' ? 'border-green-500' : 
                          result === 'loss' ? 'border-red-500' : 'border-yellow-500'
                        }`}
                      >
                        <div className="text-center space-y-3">
                          <span className={`inline-block text-xs font-bold px-3 py-1 rounded-full ${
                            result === 'win' ? 'bg-green-100 text-green-700' : 
                            result === 'loss' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            {result === 'win' ? 'VITTORIA' : result === 'loss' ? 'SCONFITTA' : 'PAREGGIO'}
                          </span>
                          
                          <div className={`font-bold text-lg ${match.home_team.toLowerCase().includes('volpiano') ? 'text-[#0047AB]' : 'text-slate-800'}`}>
                            {match.home_team}
                          </div>
                          
                          <div className="bg-[#0F172A] text-white py-3 px-6 rounded-xl inline-block">
                            <span className="font-accent text-3xl font-bold">
                              {match.home_score} - {match.away_score}
                            </span>
                          </div>
                          
                          <div className={`font-bold text-lg ${match.away_team.toLowerCase().includes('volpiano') ? 'text-[#0047AB]' : 'text-slate-800'}`}>
                            {match.away_team}
                          </div>
                          
                          {match.date && (
                            <p className="text-slate-500 text-sm">
                              {new Date(match.date).toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })()
                ) : (
                  <div className="bg-white rounded-xl p-6 text-center text-slate-500">
                    Nessun risultato disponibile
                  </div>
                )}
              </div>
            </div>

            {/* Classifica */}
            <div>
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-[#0047AB]" />
                  <h3 className="text-xl font-bold text-slate-900">Classifica</h3>
                </div>
                <a 
                  href="https://torino.iamcalcio.it/classifiche/14/eccellenza-girone-a/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#0047AB] hover:underline text-xs flex items-center gap-1"
                >
                  Completa <ExternalLink className="w-3 h-3" />
                </a>
              </div>
              <div className="bg-white rounded-xl border overflow-hidden">
                {loading ? (
                  <div className="animate-pulse p-4 space-y-2">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <div key={i} className="h-8 bg-slate-200 rounded" />
                    ))}
                  </div>
                ) : classifica.length > 0 ? (
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-3 py-2 text-left font-semibold text-slate-600">#</th>
                        <th className="px-3 py-2 text-left font-semibold text-slate-600">Squadra</th>
                        <th className="px-3 py-2 text-center font-semibold text-slate-600">Pt</th>
                      </tr>
                    </thead>
                    <tbody>
                      {classifica.slice(0, 8).map((team, idx) => (
                        <tr 
                          key={idx} 
                          className={`border-t ${team.is_volpiano ? 'bg-blue-50 font-bold' : ''}`}
                        >
                          <td className="px-3 py-2 text-slate-600">{team.position}</td>
                          <td className={`px-3 py-2 ${team.is_volpiano ? 'text-[#0047AB]' : 'text-slate-800'}`}>
                            {team.team}
                          </td>
                          <td className="px-3 py-2 text-center font-bold">{team.points}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="p-6 text-center text-slate-500">
                    Classifica non disponibile
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* News Section */}
      <section
        className="section-padding bg-slate-50"
        data-testid="news-section"
      >
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl lg:text-3xl font-bold text-slate-900">
              Ultime Notizie
            </h2>
            <Link
              to="/news"
              data-testid="view-all-news"
              className="text-[#0047AB] hover:underline text-sm font-medium flex items-center gap-1"
            >
              Vedi tutte <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="animate-pulse bg-white rounded-xl overflow-hidden"
                >
                  <div className="h-48 bg-slate-200" />
                  <div className="p-6 space-y-3">
                    <div className="h-4 bg-slate-200 rounded w-3/4" />
                    <div className="h-3 bg-slate-200 rounded w-full" />
                    <div className="h-3 bg-slate-200 rounded w-2/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : articles.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {articles.slice(0, 3).map((article, index) => (
                <Link
                  key={article.id}
                  to={`/news/${article.id}`}
                  data-testid={`news-card-${article.id}`}
                  className={`card-hover bg-white rounded-xl overflow-hidden shadow-sm ${
                    index === 0 ? "md:col-span-2 lg:col-span-1" : ""
                  }`}
                >
                  <div className="aspect-[4/3] overflow-hidden bg-slate-100">
                    <img
                      src={
                        article.image_url ||
                        "https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=800"
                      }
                      alt={article.title}
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div className="p-6">
                    <span className="text-xs font-medium text-[#0047AB] bg-blue-50 px-2 py-1 rounded-full">
                      {article.category === "match_report"
                        ? "Report"
                        : article.category === "announcement"
                        ? "Annuncio"
                        : "News"}
                    </span>
                    <h3 className="font-bold text-lg mt-3 mb-2 text-slate-900 line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="text-slate-600 text-sm line-clamp-2">
                      {article.excerpt}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <p className="text-slate-500 text-center py-12">
              Nessuna notizia disponibile
            </p>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section
        className="relative py-16 lg:py-24 overflow-hidden"
        data-testid="cta-section"
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(https://images.pexels.com/photos/8941573/pexels-photo-8941573.jpeg?w=1920)`,
          }}
        />
        <div className="hero-overlay absolute inset-0" />
        <div className="relative z-10 max-w-3xl mx-auto px-4 text-center text-white">
          <h2 className="text-3xl lg:text-4xl font-bold mb-4">
            Unisciti alla Famiglia Volpianopianese
          </h2>
          <p className="text-slate-300 mb-8">
            Iscrizioni aperte per la Scuola Calcio. Dai 5 ai 16 anni, per tutti
            i livelli.
          </p>
          <Link
            to="/contatti"
            data-testid="cta-contact"
            className="btn-primary inline-flex items-center gap-2"
          >
            Contattaci
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
